// Тема (светлая, тёмная, как в телефоне), фон приложения, приветствие на «Сегодня» с фразой дня.
import { saveSettings, setThemeApplier } from './api.ts';
import { esc, store } from './dom.ts';
import { chooseSheet, promptSheet } from './sheet.ts';
import { S } from './state.ts';

export const THEMES: Array<[string, string]> = [['system', 'как в телефоне'], ['light', 'светлая'], ['dark', 'тёмная']];
export function themeLabel(v: string): string { return (THEMES.find((x) => x[0] === v) ?? THEMES[0]!)[1]; }

// мостик приложения для Android (APK): цвет строки состояния, выход, вход и разрешение Google через браузер телефона
declare global {
  interface Window { Praktika?: { setTheme?: (t: string) => void; logout?: () => void; login?: () => void;
    auth?: (what: string, hint: string) => void } }
}

export function applyTheme(mode: string): void {
  const m = mode || 'system';
  const dark = m === 'dark' || (m === 'system' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  // фон приложения из настроек (Ещё → Внешний вид); до загрузки данных — из памяти телефона
  let bg = '';
  try {
    const st = S?.settings ? { light: S.settings.bg_light || '', dark: S.settings.bg_dark || '' } :
      (JSON.parse(store('praktika_bg') || '{}') as { light?: string; dark?: string });
    bg = dark ? (st.dark || '') : (st.light || '');
    if (S?.settings) store('praktika_bg', JSON.stringify(st));
  } catch { /* нет памяти — обычный фон */ }
  if (/^#[0-9a-f]{6}$/i.test(bg)) document.documentElement.style.setProperty('--bg', bg);
  else document.documentElement.style.removeProperty('--bg');
  const mt = document.querySelector('meta[name="theme-color"]');
  if (mt) mt.setAttribute('content', /^#[0-9a-f]{6}$/i.test(bg) ? bg : (dark ? '#0f131a' : '#f3f5f8'));
  store('praktika_theme', m);
  try { window.Praktika?.setTheme?.(dark ? 'dark' : 'light'); } catch { /* не в приложении-обёртке */ }
}
setThemeApplier(applyTheme);

export function initTheme(): void {
  applyTheme(store('praktika_theme') || 'system');
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change',
      () => applyTheme(S?.settings?.theme || 'system'));
  } catch { /* старый браузер */ }
}

export const PHRASES = ['Пусть день будет лёгким', 'Сегодня всё получится', 'Спокойного и ясного дня', 'Вы делаете важное дело',
  'Хорошего дня и тёплых встреч', 'Пусть работа радует', 'Сил, тепла и вдохновения', 'Бережного дня к себе',
  'Сегодня хороший день', 'Пусть всё сложится мягко',
  'Лёгкого дыхания сегодня', 'Вы на своём месте', 'Пусть будет время на паузу', 'Тёплого и спокойного дня',
  'Доверяйте себе сегодня', 'Пусть день принесёт радость', 'Ваш труд меняет жизни', 'Ясной головы и тепла',
  'Пусть встречи будут глубокими', 'Сегодня можно не спешить',
  'Не забудьте про себя', 'Пусть хватит сил на всё', 'Хорошего настроения с утра', 'Вы справитесь, как всегда',
  'Пусть день будет добрым', 'Немного солнца в каждый час', 'Спокойствия и уверенности', 'Пусть работа идёт легко',
  'Сегодня получится важное', 'Улыбнитесь, день начался',
  'Пусть клиенты радуют', 'Тишины внутри и ясности', 'Вы умеете быть рядом', 'Пусть будет повод улыбнуться',
  'Лёгких слов и тёплых пауз', 'Сегодня всё по силам', 'Пусть день пройдёт ровно', 'Вы важны и нужны',
  'Берегите себя сегодня', 'Пусть вдохновение найдёт вас',
  'Хороших новостей сегодня', 'Мягкого дня и вкусного чая', 'Пусть время будет добрым', 'Вы делаете мир теплее',
  'Сегодня день маленьких побед', 'Пусть всё идёт своим чередом', 'Спокойного сердца сегодня',
  'Радости в простых вещах', 'Пусть усталость обходит стороной', 'Вы отлично справляетесь',
  'Пусть слова находятся сами', 'Света, тепла и сил', 'Сегодня вы молодец', 'Пусть день удивит приятно',
  'Дышите глубже, всё хорошо', 'Пусть сессии пройдут легко', 'Гармонии в делах и мыслях',
  'Вы помогаете людям расти', 'Пусть хватит времени на себя', 'Добрых встреч сегодня',
  'Пусть всё сложится удачно', 'Ясного взгляда и терпения', 'Сегодня можно гордиться собой', 'Пусть будет уютно внутри',
  'Тёплого чая между сессиями', 'Вы вдохновляете других', 'Пусть день будет щедрым', 'Лёгкости в каждом шаге',
  'Сегодня всё на своих местах', 'Пусть работа приносит смысл',
  'Спокойной уверенности сегодня', 'Вы умеете слышать', 'Пусть день наполнит силами', 'Мира в душе и делах',
  'Сегодня хороший день для добра', 'Пусть всё получится бережно', 'Радостных мелочей сегодня',
  'Вы нужны этому миру', 'Пусть перерывы будут настоящими', 'Устойчивости и мягкости',
  'Сегодня будет хорошо', 'Пусть усилия вернутся теплом', 'Вы растёте вместе с клиентами', 'Пусть будет больше света',
  'Доброго дня и ясных мыслей', 'Сегодня день для важного', 'Пусть ничего не тревожит', 'Вы достойны отдыха тоже',
  'Пусть каждая встреча согреет', 'Лёгкого старта и финиша',
  'Сегодня всё сложится', 'Пусть день будет вашим', 'Тепла вам и вашим близким', 'Вы делаете больше, чем кажется',
  'Пусть работа будет в радость', 'Спокойного ритма сегодня', 'Сегодня мир на вашей стороне',
  'Пусть сил хватит с запасом', 'Вы умеете поддержать', 'Хорошего дня, всё получится'];

// фраза дня: случайная, но не та же, что в прошлый раз
export const phrase = { text: (() => {
  const last = Number(store('praktika_phrase') ?? -1);
  let i = Math.floor(Math.random() * PHRASES.length);
  if (i === last) i = (i + 1) % PHRASES.length;
  store('praktika_phrase', String(i));
  return PHRASES[i]!;
})() };

export function helloHtml(n: { min: number }): string {
  const h = Math.floor(n.min / 60),
    hi = h < 5 ? 'Доброй ночи' : h < 12 ? 'Доброе утро' : h < 18 ? 'Добрый день' : 'Добрый вечер',
    nm = String(S.settings.user_name || '').trim();
  return '<button class="hello" data-uname><div class="hi">' + esc(hi + (nm ? ', ' + nm : '') + '!') +
    '</div><div class="ph">' + esc(phrase.text) + '</div>' +
    (nm ? '' : '<div class="ask">Нажмите, чтобы приложение обращалось к вам по имени</div>') + '</button>';
}

export function askUserName(): void {
  promptSheet('Как к вам обращаться', S.settings.user_name || '', 'text',
    (v) => { void saveSettings({ user_name: String(v || '').trim() }, 'Приятно познакомиться'); });
}

document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (t.closest?.('[data-uname]')) { askUserName(); return; }
  if (t.closest?.('[data-theme-pick]')) {
    chooseSheet('Оформление', THEMES.map((x) => ({ value: x[0], label: x[1] })), S.settings.theme || 'system',
      (v) => { applyTheme(v); void saveSettings({ theme: v as 'system' | 'light' | 'dark' }); });
  }
});
